import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Sparkles, Plus, Trash2, Check, Loader2, AlertTriangle, RotateCcw, RotateCw, Crop, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi, useLoad } from "@/lib/api";
import { useMe } from "@/lib/me";
import { gbp, toPence, lineTotal, today, refProblem } from "@/lib/format";
import { PageHead } from "@/components/Bits";
import InstallButton from "@/components/InstallButton";
import Turned from "@/components/Turned";
import PageStrip from "@/components/PageStrip";
import DatePicker from "@/components/DatePicker";

const blank = () => ({ kind: "invoice", supplier: "", ref: "", dn_no: "", date: today(), amount: "", lines: [] });

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>
);

function LineEditor({ lines, setLines }) {
  const upd = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="space-y-2" data-testid="line-items-editor">
      {lines.length > 0 && (
        <div className="hidden grid-cols-12 gap-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sm:grid" data-testid="line-items-head">
          <span className="col-span-5">Product</span><span className="col-span-2">Qty</span><span className="col-span-2">Unit £</span><span className="col-span-2">VAT</span>
        </div>
      )}
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 rounded-xl bg-slate-50 p-2" data-testid={`line-item-${i}`}>
          <Input className="col-span-12 sm:col-span-5" placeholder="Product" value={l.product} onChange={(e) => upd(i, "product", e.target.value)} data-testid={`line-product-${i}`} />
          <Input className="col-span-3 sm:col-span-2" inputMode="numeric" placeholder="Qty" value={l.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`line-qty-${i}`} />
          <Input className="col-span-4 sm:col-span-2" inputMode="decimal" placeholder="Unit £" value={l.unit} onChange={(e) => upd(i, "unit", e.target.value)} data-testid={`line-unit-${i}`} />
          <select className="col-span-3 sm:col-span-2 h-9 rounded-md border border-input bg-white px-2 text-sm" value={l.vat} onChange={(e) => upd(i, "vat", e.target.value)} data-testid={`line-vat-${i}`}>
            <option value="0">0%</option><option value="5">5%</option><option value="20">20%</option>
          </select>
          <Button type="button" variant="ghost" size="icon" className="col-span-2 sm:col-span-1" onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, { product: "", qty: "1", unit: "", vat: "0" }])} data-testid="add-line-button">
        <Plus className="mr-1 h-4 w-4" /> Add line
      </Button>
    </div>
  );
}

function Recent({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-10 rise">
      <h2 className="mb-3 text-lg font-bold">Your recent uploads</h2>
      <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="recent-uploads-list">
        {items.slice(0, 8).map((d) => (
          <div key={d.id} className="flex items-center justify-between px-4 py-3 text-sm">
            <div><p className="font-mono font-semibold">{d.ref}</p><p className="text-xs text-slate-500">{d.supplier} · {d.kind === "invoice" ? "Invoice" : "Credit claim"} · {d.date}</p></div>
            <p className="font-mono font-semibold">{gbp(d.amount)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const b64File = (b64, name) => {
  const bin = atob(b64);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return new File([u], name, { type: "image/jpeg" });
};
let seq = 0;

export default function UploadPage() {
  const api = useApi();
  const me = useMe();
  const fileRef = useRef();
  const galleryRef = useRef();
  const pagesRef = useRef([]);
  const readSeq = useRef(0);
  const touched = useRef(new Set());
  const [f, setF] = useState(blank());
  const [pages, setPagesState] = useState([]);
  const [sel, setSel] = useState(0);
  const [aiTurned, setAiTurned] = useState(false);
  const [reading, setReading] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [pageInfo, setPageInfo] = useState(null);
  const suppliers = useLoad("/suppliers");
  const refErr = f.kind === "invoice" ? refProblem((suppliers.data || []).find((s) => s.name.toLowerCase() === f.supplier.trim().toLowerCase()), f.ref) : null;
  const recent = useLoad("/docs");
  const set = (k) => (e) => { touched.current.add(k); setF({ ...f, [k]: e.target.value }); };
  const setPages = (fn) => { pagesRef.current = typeof fn === "function" ? fn(pagesRef.current) : fn; setPagesState(pagesRef.current); };
  const upd = (id, patch) => setPages((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const readAll = async () => {
    const list = pagesRef.current;
    if (!me.features.photo_reading || !list.length) return;
    const mine = ++readSeq.current;
    setReading(true); setAiNote("");
    try {
      const fd = new FormData(); list.forEach((p) => fd.append("images", p.file));
      const j = await api("post", "/extract", fd);
      if (mine !== readSeq.current) return;
      setPageInfo(j.pages_total && j.pages_total > list.length || j.missing_pages?.length
        ? { total: j.pages_total, missing: j.missing_pages || [], known: (j.page_numbers || []).every((n) => n) } : null);
      const keep = (k, v) => (touched.current.has(k) ? undefined : v);
      setF((cur) => {
        const next = {
          kind: j.kind || cur.kind, supplier: j.supplier || cur.supplier, ref: j.invoice_no || cur.ref, dn_no: j.dn_no || cur.dn_no, date: j.date || cur.date,
          amount: j.total != null ? String(j.total) : cur.amount,
          lines: (j.lines || []).map((l) => ({ product: l.product, qty: String(l.qty), unit: (l.unit / 100).toFixed(2), vat: String(l.vat) })),
        };
        // a re-read never overwrites what the user has already changed
        return { ...cur, ...Object.fromEntries(Object.entries(next).filter(([k, v]) => keep(k, v) !== undefined)) };
      });
      let turned = false;
      setPages((ps) => ps.map((p) => {
        const i = list.findIndex((q) => q.id === p.id);
        if (i < 0 || p.manual || !p.preview) return p;
        const r = (j.rotations || [])[i] || 0;
        turned = turned || r > 0;
        return { ...p, rotation: r };
      }));
      setAiTurned(turned); setAiNote("ok");
    } catch (err) {
      if (mine === readSeq.current) setAiNote("fail");
    } finally { if (mine === readSeq.current) setReading(false); }
  };

  const pick = async (e) => {
    const fl = e.target.files?.[0];
    e.target.value = "";
    if (!fl) return;
    const isPdf = !fl.type.startsWith("image/");
    if (pagesRef.current.length && (isPdf || !pagesRef.current[0].preview)) return toast.error("A PDF has to be uploaded on its own");
    const id = ++seq;
    setPages((ps) => [...ps, { id, file: fl, original: fl, preview: isPdf ? null : URL.createObjectURL(fl), rotation: 0, manual: false, cropped: false, cropFile: null, trimming: !isPdf }]);
    setSel(pagesRef.current.length - 1);
    if (!isPdf) {
      try {
        const fd = new FormData(); fd.append("image", fl);
        const r = await api("post", "/docs/prepare", fd);
        if (r.cropped) { const cf = b64File(r.image, `page-${id}.jpg`); upd(id, { file: cf, cropFile: cf, preview: URL.createObjectURL(cf), cropped: true }); }
      } catch (err) { /* keep the original photo */ }
      upd(id, { trimming: false });
    }
    if (pagesRef.current.some((p) => p.id === id)) readAll();
  };

  const toggleCrop = (p) => {
    const file = p.cropped ? p.original : p.cropFile;
    upd(p.id, { file, preview: URL.createObjectURL(file), cropped: !p.cropped });
  };
  const removePage = (i) => { setPages((ps) => ps.filter((_, j) => j !== i)); setSel(0); readAll(); };
  const reset = () => { readSeq.current++; touched.current = new Set(); setF(blank()); setPages([]); setSel(0); setReading(false); setAiNote(""); setAiTurned(false); setPageInfo(null); };

  const save = async (e) => {
    e.preventDefault();
    if (!pages.length) return toast.error("Take or choose a photo first");
    if (pageInfo && !window.confirm(`This invoice looks like it has ${pageInfo.total || "more"} pages but you've photographed ${pages.length}. Save without the missing page?`)) return;
    const lines = f.kind === "invoice" ? f.lines.filter((l) => l.product.trim()) : [];
    const fd = new FormData();
    if (pageInfo?.total) fd.append("pages_expected", String(pageInfo.total));
    Object.entries({ kind: f.kind, supplier: f.supplier, ref: f.ref, dn_no: f.kind === "invoice" ? f.dn_no : "", date: f.date, amount: f.amount }).forEach(([k, v]) => fd.append(k, v));
    fd.append("lines", JSON.stringify(lines.map((l) => ({ product: l.product, qty: Number(l.qty), unit: toPence(l.unit), vat: Number(l.vat) }))));
    fd.append("rotations", JSON.stringify(pages.map((p) => (p.preview ? p.rotation % 360 : 0))));
    pages.forEach((p) => fd.append("images", p.file));
    setSaving(true);
    try {
      const d = await api("post", "/docs", fd);
      toast.success(`Saved ${d.ref} · ${gbp(d.amount)}${pages.length > 1 ? ` · ${pages.length} pages` : ""}`);
      reset(); recent.reload();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const calc = lineTotal(f.lines.filter((l) => l.product));
  const off = f.kind === "invoice" && f.lines.length > 0 && Math.abs(calc - toPence(f.amount)) > 2;
  const cur = pages[Math.min(sel, pages.length - 1)];
  const trimming = pages.some((p) => p.trimming);
  const pill = "absolute flex items-center gap-1 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold";

  return (
    <div className="max-w-2xl">
      <PageHead title="Upload" sub="Photograph an invoice or credit claim, check the details, save. Long invoices can have several pages." />
      <InstallButton className="mb-6 w-full md:hidden" />
      <form onSubmit={save} className="space-y-6 rise">
        <input ref={fileRef} type="file" accept="image/*,.pdf" capture="environment" className="hidden" onChange={pick} data-testid="file-input-invoice" />
        <input ref={galleryRef} type="file" accept="image/*,.pdf" className="hidden" onChange={pick} data-testid="file-input-gallery" />
        {!cur ? (
          <div className="space-y-3">
            <button type="button" onClick={() => fileRef.current.click()} data-testid="camera-upload-button"
              className="group relative flex min-h-[46vh] w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-[28px] border border-slate-200 bg-white px-6 py-14 transition-colors hover:border-teal-500 md:min-h-0">
              {["left-5 top-5 border-l-2 border-t-2 rounded-tl-xl", "right-5 top-5 border-r-2 border-t-2 rounded-tr-xl", "bottom-5 left-5 border-b-2 border-l-2 rounded-bl-xl", "bottom-5 right-5 border-b-2 border-r-2 rounded-br-xl"].map((c) => <span key={c} className={`absolute h-10 w-10 border-teal-600 ${c}`} />)}
              <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-teal-600 text-white shadow-[0_12px_40px_-10px_rgba(13,148,136,0.7)] transition-transform group-hover:scale-105">
                <span className="absolute inset-0 animate-ping rounded-full bg-teal-500/30" /><Camera className="relative h-8 w-8" />
              </span>
              <span className="font-display text-2xl font-bold">Take photo</span>
              <span className="max-w-xs text-center text-sm text-slate-500">Lay the invoice flat and fill the frame. We'll straighten and read it.</span>
            </button>
            <button type="button" onClick={() => galleryRef.current.click()} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-400" data-testid="gallery-upload-button">
              <ImageIcon className="h-4 w-4" /> Choose a picture or PDF
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-black" data-testid="photo-preview">
              {cur.preview ? <Turned src={cur.preview} rotation={cur.rotation} /> : <p className="p-10 text-center text-sm text-slate-300">{cur.file.name}</p>}
              {(reading || cur.trimming) && <div className="scanline" />}
              {cur.preview && (
                <button type="button" onClick={() => upd(cur.id, { rotation: cur.rotation + 90, manual: true })} className={`${pill} left-3 top-3`} data-testid="rotate-photo-button">
                  <RotateCw className="h-3.5 w-3.5" /> Rotate
                </button>
              )}
              <button type="button" onClick={reset} className={`${pill} right-3 top-3`} data-testid="retake-photo-button">
                <RotateCcw className="h-3.5 w-3.5" /> Start again
              </button>
              {cur.cropFile && (
                <button type="button" onClick={() => toggleCrop(cur)} className={`${pill} bottom-3 left-3`} data-testid="crop-toggle-button">
                  <Crop className="h-3.5 w-3.5" /> {cur.cropped ? "Undo crop" : "Crop to paper"}
                </button>
              )}
              {pages.length > 1 && <span className="absolute bottom-3 right-3 rounded-full bg-slate-900/80 px-2.5 py-1 text-xs font-bold text-white" data-testid="page-indicator">Page {sel + 1} of {pages.length}</span>}
            </div>
            <PageStrip pages={pages} sel={sel} onSelect={setSel} onRemove={removePage} onAdd={() => fileRef.current.click()}
              canAdd={!!pages[0].preview && pages.length < 10 && !trimming} />
          </div>
        )}

        {trimming && <p className="flex items-center gap-2 text-sm font-medium text-teal-700" data-testid="trimming-status"><Loader2 className="h-4 w-4 animate-spin" /> Trimming the background…</p>}
        {reading && !trimming && <p className="flex items-center gap-2 text-sm font-medium text-teal-700" data-testid="ai-reading-status"><Loader2 className="h-4 w-4 animate-spin" /> Reading {pages.length > 1 ? `all ${pages.length} pages` : "the photo"}…</p>}
        {aiNote === "ok" && <p className="flex items-center gap-2 rounded-xl bg-teal-50 px-3 py-2 text-sm text-teal-800" data-testid="ai-autofill-ok"><Sparkles className="h-4 w-4" /> Filled in from {pages.length > 1 ? `all ${pages.length} pages` : "the photo"}{aiTurned ? " and turned upright" : ""}. Please check every detail before saving.</p>}
        {aiNote === "fail" && <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="ai-autofill-failed">Couldn't read the photo — please type the details.</p>}
        {pageInfo && !reading && !trimming && (
          <div className="flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="missing-page-alert">
            <p className="flex items-start gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{pageInfo.known && pageInfo.missing.length
                ? `Page ${pageInfo.missing.join(", ")} of ${pageInfo.total} is missing.`
                : `This invoice has ${pageInfo.total} pages but only ${pages.length} ${pages.length === 1 ? "is" : "are"} photographed.`} Add the missing page so every item is read.</span>
            </p>
            {pages[0]?.preview && pages.length < 10 && (
              <Button type="button" size="sm" className="shrink-0 bg-amber-600 hover:bg-amber-700" onClick={() => fileRef.current.click()} data-testid="missing-page-add-button">
                <Camera className="mr-1 h-4 w-4" /> Add page
              </Button>
            )}
          </div>
        )}
        {!me.features.photo_reading && cur && <p className="text-xs text-slate-500" data-testid="ai-disabled-note">Photo reading is off — type the details below.</p>}

        <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
          {[["invoice", "Invoice"], ["credit_claim", "Credit claim"]].map(([v, l]) => (
            <button type="button" key={v} onClick={() => { touched.current.add("kind"); setF({ ...f, kind: v }); }} data-testid={`kind-${v}-button`}
              className={`rounded-xl py-2.5 text-sm font-semibold transition-colors ${f.kind === v ? "bg-white shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Wholesaler">
            <Input list="supplier-list" required value={f.supplier} onChange={set("supplier")} placeholder="e.g. Alliance Healthcare" data-testid="upload-supplier-input" />
            <datalist id="supplier-list">{(suppliers.data || []).map((s) => <option key={s.name} value={s.name} />)}</datalist>
          </Field>
          <Field label={f.kind === "invoice" ? "Invoice number" : "Claim number"}>
            <Input required={f.kind !== "invoice" || !f.dn_no.trim()} value={f.ref} onChange={set("ref")} className="font-mono" data-testid="upload-ref-input" />
            {refErr && <p className="mt-1 text-xs font-semibold text-rose-700" data-testid="upload-ref-format-error">{refErr}</p>}
            {f.kind === "invoice" && !f.ref.trim() && f.dn_no.trim() && <p className="mt-1 text-xs text-amber-700" data-testid="upload-no-invoice-note">No invoice number? It'll be linked to the statement and the owner approves the match.</p>}
          </Field>
          {f.kind === "invoice" && (
            <Field label="Delivery note number">
              <Input value={f.dn_no} onChange={set("dn_no")} className="font-mono" placeholder="if printed" data-testid="upload-dn-input" />
            </Field>
          )}
          <Field label="Date"><DatePicker value={f.date} onChange={(v) => { touched.current.add("date"); setF({ ...f, date: v }); }} testid="upload-date-input" /></Field>
          <Field label="Total incl. VAT (£)"><Input required inputMode="decimal" value={f.amount} onChange={set("amount")} placeholder="0.00" className="font-mono" data-testid="upload-amount-input" /></Field>
        </div>

        {f.kind === "invoice" && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Line items (optional)</Label>
            <LineEditor lines={f.lines} setLines={(lines) => { touched.current.add("lines"); setF({ ...f, lines }); }} />
            {f.lines.length > 0 && (
              <p className={`flex flex-wrap items-center gap-2 text-sm ${off ? "text-amber-700" : "text-slate-500"}`} data-testid="line-total-check">
                {off && <AlertTriangle className="h-4 w-4" />} Lines add up to <b className="font-mono">{gbp(calc)}</b> incl. VAT · invoice total <b className="font-mono">{gbp(toPence(f.amount))}</b>{off ? " — doesn't match" : ""}
                {off && <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => { touched.current.add("amount"); setF({ ...f, amount: (calc / 100).toFixed(2) }); }} data-testid="use-lines-total-button">Use lines total</Button>}
              </p>
            )}
          </div>
        )}

        <div className={cur ? "sticky bottom-[84px] z-20 md:static" : ""}>
          <Button type="submit" disabled={saving || reading || trimming || !!refErr} className="h-14 w-full rounded-2xl text-base shadow-lg md:h-12 md:shadow-none" data-testid="ai-autofill-confirm-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Confirm & save
          </Button>
        </div>
      </form>
      <Recent items={recent.data} />
    </div>
  );
}
